import { useContext, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'helpers/dayjs';

import { CardContext } from 'context/cards/cardContext';
import { CARD_FORM_ERROR, CLOSE_CARD_FORM_MODAL, GET_CARDS, UPDATE_CARDS_METADATA } from 'context/cards/types';
import { Cards, Overview } from 'lib/api';
import CreateUserForm from './steps/2.SelectCardholder/CreateUserForm';
import CardType from './steps/1.CardType';
import SelectCardHolder from './steps/2.SelectCardholder/2.SelectCardholder';
import CompleteUserProfile from './steps/2.SelectCardholder/2.5.CompleteUserProfile';
import CustomizeCard from './steps/3.CustomizeCard';
import CardIdentity from './steps/4.CardIdentity';
import CardOverview from './steps/CardOverview/CardOverview';
import OrderingCard from './steps/OrderingCard';
import { REPLENISH_FRECUENCY } from 'helpers/constants';
import './CreateCard.css';

export const CARD_FORM_STEP = {
    TYPE: 'TYPE',
    CARDHOLDER: 'CARDHOLDER',
    CUSTOMIZE: 'CUSTOMIZE',
    IDENTITY: 'IDENTITY',
    ORDERING: 'ORDERING',
    OVERVIEW: 'OVERVIEW',
    EDIT_USER: 'EDIT_USER'
};
const CARD_VALUES_TO_CREATE = {
    type: '',
    cardholderId: null,
    department: '',
    label: '',
    limitAmount: 0,
    memo: '',
    billingAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
    },
    autoLock: '',
    has_manual_replenish: REPLENISH_FRECUENCY.ONE_TIME
};
const CreateCard = () => {
    const { card, cards, dispatch, isCardFormModalOpen, isFormSubmitting, isUserFormModalOpen } = useContext(CardContext);
    const [cardForm, setCardForm] = useState(card);
    const [formStep, setFormStep] = useState(CARD_FORM_STEP.TYPE);
    const [cardholder, setCardholder] = useState(null);
    const [createdCard, setCreatedCard] = useState();

    useEffect(() => {
        setCardForm(card);
    }, [card]);

    const handleCardFormModalClose = (event, reason) => {
        if (isFormSubmitting) return;

        dispatch({ type: CLOSE_CARD_FORM_MODAL });
        setFormStep(CARD_FORM_STEP.TYPE);
        setCardForm(card);
    };

    const handleSubmit = async (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(CARD_FORM_STEP.ORDERING);
        try {
            await createCard(values);
            setFormStep(CARD_FORM_STEP.OVERVIEW);
        } catch (error) {
            setFormStep(CARD_FORM_STEP.IDENTITY);
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
    };
    const createCard = async values => {
        const body = mapValuesToCreateCardModel(values);
        const cardCreated = await Cards.createCard(body);
        setCreatedCard(cardCreated);
        dispatch({ type: GET_CARDS });

        const overviewInfo = await Overview.getOverviewInfo();
        dispatch({
            type: UPDATE_CARDS_METADATA,
            payload: overviewInfo
        });

        // Push to Google tag manager
        window.dataLayer.push({
            event: cards.length > 0 ? 'virtual-card-created' : 'first-virtual-card-created'
        });
    };
    const mapValuesToCreateCardModel = values => {
        const cardValues = cloneDeep(values);
        const body = {};
        for (const key of Object.keys(cardValues)) {
            if (key in CARD_VALUES_TO_CREATE) {
                body[key] = cardValues[key];
                if (key === 'autoLock' && cardValues[key]) {
                    body[key] = dayjs(cardValues[key]).tz('America/Los_Angeles').startOf('day').toISOString();
                }
                if (key === 'has_manual_replenish') {
                    body[key] = cardValues[key] ? true : false;
                }
            }
        }
        return body;
    };

    const handleBackClick = currentStep => {
        setFormStep(currentStep);
    };

    const handleStepSubmit = (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(getNextStep());
    };

    const getNextStep = () => {
        if (formStep === CARD_FORM_STEP.TYPE) return CARD_FORM_STEP.CARDHOLDER;
        // If cardholder is incomplete next step should be Edit User screen.
        if (formStep === CARD_FORM_STEP.CARDHOLDER && cardholder?.is_incomplete) return CARD_FORM_STEP.EDIT_USER;
        if (formStep === CARD_FORM_STEP.CARDHOLDER) return CARD_FORM_STEP.CUSTOMIZE;
        // If incomplete user was sucessfully edited, normal flow should continue.
        if (formStep === CARD_FORM_STEP.EDIT_USER) return CARD_FORM_STEP.CUSTOMIZE;
        if (formStep === CARD_FORM_STEP.CUSTOMIZE) return CARD_FORM_STEP.IDENTITY;
        if (formStep === CARD_FORM_STEP.IDENTITY) return CARD_FORM_STEP.ORDERING;
        if (formStep === CARD_FORM_STEP.ORDERING) return CARD_FORM_STEP.OVERVIEW;
    };

    const handleDoneClick = () => {
        setCardForm(card);
        setFormStep(CARD_FORM_STEP.TYPE);
        dispatch({ type: CLOSE_CARD_FORM_MODAL });
    };

    return (
        <>
            <Dialog open={isCardFormModalOpen} onClose={() => {}} fullWidth maxWidth="md" sx={{ zIndex: 2 }}>
                {formStep === CARD_FORM_STEP.TYPE && (
                    <CardType
                        card={cardForm}
                        setCardForm={setCardForm}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.CARDHOLDER && (
                    <SelectCardHolder
                        card={cardForm}
                        setCardholder={setCardholder}
                        onBackBtnClick={handleBackClick}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.EDIT_USER && (
                    <CompleteUserProfile
                        card={cardForm}
                        cardholder={cardholder}
                        setCardholder={setCardholder}
                        onBackBtnClick={handleBackClick}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.CUSTOMIZE && (
                    <CustomizeCard
                        card={cardForm}
                        cardholder={cardholder}
                        onBackBtnClick={handleBackClick}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.IDENTITY && (
                    <CardIdentity
                        card={cardForm}
                        cardholder={cardholder}
                        onBackBtnClick={handleBackClick}
                        onOrderCardClick={handleSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.ORDERING && (
                    <OrderingCard card={cardForm} cardholder={cardholder} onCardModalClose={handleCardFormModalClose} />
                )}
                {formStep === CARD_FORM_STEP.OVERVIEW && (
                    <CardOverview
                        createdCard={createdCard}
                        card={cardForm}
                        cardholder={cardholder}
                        onDoneClick={handleDoneClick}
                    />
                )}
            </Dialog>
            {isUserFormModalOpen && <CreateUserForm />}
        </>
    );
};

export default CreateCard;
